import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { SVG, Loader } from 'Components';
import { ROUTES, SVG_TYPE } from 'Shared/enums';
import { MainType } from './Container';

const QUESTION_TYPE = {
  basic: 'PODSTAWOWY',
  specialized: 'SPECJALISTYCZNY',
};
const mapStoredAnswerToOption = (question, storedAnswer) => {
  if (!storedAnswer) return storedAnswer;

  if (question?.type === QUESTION_TYPE.specialized) {
    if (storedAnswer === 'A') return 'answerA';
    if (storedAnswer === 'B') return 'answerB';
    if (storedAnswer === 'C') return 'answerC';
    return storedAnswer;
  }

  return storedAnswer;
};

function FastTrackExaminationPage(props: MainType) {
  const {
    actions: { getDrivingTest, getDrivingTestAnswer, finishDrivingTest, saveTask },
    drivingTest,
  } = props;
  const navigate = useNavigate();

  const [isStart, setIsStart] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isFinishing, setIsFinishing] = useState(false);
  const [isAnswerLoading, setIsAnswerLoading] = useState(false);
  const [isBookmarkLoading, setIsBookmarkLoading] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
  const [answerResults, setAnswerResults] = useState<Record<number, { isAnsweredCorrectly: boolean; correctAnswer?: string; userAnswer?: string }>>(
    {},
  );
  const [maxVisibleIndex, setMaxVisibleIndex] = useState<number>(0);
  const [savedQuestions, setSavedQuestions] = useState<Record<string, boolean>>({});
  const [showSwipeNote, setShowSwipeNote] = useState<boolean>(false);
  const [mediaLoading, setMediaLoading] = useState<Record<number, boolean>>({});

  const hintTimeoutRef = useRef<number | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const didScrollOnEnterRef = useRef<boolean>(false);
  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});
  const fetchControllers = useRef<Record<number, AbortController>>({});
  const fetchedVideosRef = useRef<Record<number, boolean>>({});
  const isSnapCooldownRef = useRef<boolean>(false);
  const touchStartYRef = useRef<number | null>(null);

  const questions = useMemo(() => drivingTest?.question || [], [drivingTest]);
  const totalQuestions = questions.length;
  const currentQuestion = questions[questionIndex];
  const currentQuestionResult = answerResults[questionIndex];
  const isCurrentQuestionSaved = Boolean(currentQuestion?.uuid && savedQuestions[currentQuestion.uuid]);
  const mediaPrefetchLimit = questionIndex + 2;

  const restoreExamProgress = useCallback((examQuestions) => {
    if (!examQuestions || examQuestions.length === 0) {
      return;
    }

    const restoredSelectedAnswers: Record<number, string> = {};
    const restoredAnswerResults: Record<number, { isAnsweredCorrectly: boolean; correctAnswer?: string; userAnswer?: string }> = {};

    examQuestions.forEach((question, index) => {
      if (question?.isAnswered) {
        const storedAnswer = question?.answer;
        if (storedAnswer) {
          restoredSelectedAnswers[index] = mapStoredAnswerToOption(question, storedAnswer);
        }

        const mapped = mapStoredAnswerToOption(question, storedAnswer);
        restoredAnswerResults[index] = {
          isAnsweredCorrectly: Boolean(question?.isAnsweredCorrectly),
          userAnswer: mapped,
          correctAnswer: Boolean(question?.isAnsweredCorrectly) ? mapped : undefined,
        };
      }
    });

    const firstUnansweredIndex = examQuestions.findIndex((question) => !question?.isAnswered);
    const nextQuestionIndex = firstUnansweredIndex === -1 ? examQuestions.length - 1 : firstUnansweredIndex;

    setSelectedAnswers(restoredSelectedAnswers);
    setAnswerResults(restoredAnswerResults);
    setQuestionIndex(nextQuestionIndex >= 0 ? nextQuestionIndex : 0);
    setMaxVisibleIndex(nextQuestionIndex >= 0 ? nextQuestionIndex : 0);
    setShowSwipeNote(Boolean(restoredAnswerResults[nextQuestionIndex]));
    setIsStart(true);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      let response = drivingTest;
      if (!drivingTest) {
        response = await getDrivingTest();
      }

      if (response?.question?.length) {
        restoreExamProgress(response.question);
        return;
      }

      navigate({ pathname: ROUTES.DRIVING_TEST_SUMMARY });
    };

    fetchData();
  }, [drivingTest, getDrivingTest, navigate, restoreExamProgress]);

  useEffect(() => {
    const hintTimeout = hintTimeoutRef.current;

    return () => {
      if (hintTimeout) {
        window.clearTimeout(hintTimeout);
      }
    };
  }, []);

  const finishExam = useCallback(async () => {
    if (!drivingTest?.uuid || isFinishing) {
      return;
    }
    setIsFinishing(true);
    try {
      const response = await finishDrivingTest(drivingTest.uuid, { fastTrack: true });
      localStorage.setItem('EXAM_SUMMARY', JSON.stringify(response));
      navigate({ pathname: ROUTES.DRIVING_TEST_SUMMARY });
    } catch (err) {
      setIsFinishing(false);
    }
  }, [drivingTest?.uuid, finishDrivingTest, navigate, isFinishing]);

  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([index, videoEl]) => {
      if (!videoEl) return;

      if (Number(index) === questionIndex) {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        videoEl.play().catch(() => {});
      } else {
        videoEl.pause();
      }
    });
  }, [questionIndex]);

  // Prefetch media (images + videos) for indexes up to mediaPrefetchLimit
  useEffect(() => {
    if (!questions || !questions.length) return;

    const toPrefetch = questions.map((q, i) => ({ q, i })).filter(({ i }) => i <= mediaPrefetchLimit && i >= 0);

    toPrefetch.forEach(({ q, i }) => {
      if (!q?.filePath) return;

      const fileName = q.filePath.substring(q.filePath.lastIndexOf('/') + 1);

      if (fileName.endsWith('.webp') || fileName.match(/\.(jpe?g|png|gif|webp)$/i)) {
        // mark loading state for images
        setMediaLoading((prev) => ({ ...prev, [i]: true }));
        const img = new Image();
        img.onload = () => setMediaLoading((prev) => ({ ...prev, [i]: false }));
        img.onerror = () => setMediaLoading((prev) => ({ ...prev, [i]: false }));
        img.src = q.filePath;
      }

      if (fileName.endsWith('.mp4')) {
        // mark loading state for videos
        setMediaLoading((prev) => ({ ...prev, [i]: true }));
        // if video element already mounted, call load() to start buffer
        const v = videoRefs.current[i];
        if (v) {
          try {
            v.load();
          } catch (e) {
            // ignore
          }

          // listen for loadeddata to clear loader
          const onLoaded = () => setMediaLoading((prev) => ({ ...prev, [i]: false }));
          v.addEventListener('loadeddata', onLoaded);
          v.addEventListener('error', onLoaded);

          // cleanup listeners when unmount/prefetch changes
          setTimeout(() => {
            v.removeEventListener('loadeddata', onLoaded);
            v.removeEventListener('error', onLoaded);
          }, 30000);
        } else if (!fetchedVideosRef.current[i]) {
          // no mounted video element yet — fetch resource to warm browser cache
          fetchedVideosRef.current[i] = true; // mark as fetching/queued
          const ac = new AbortController();
          fetchControllers.current[i] = ac;
          fetch(q.filePath, { signal: ac.signal })
            .then((res) => res.arrayBuffer())
            .then(() => {
              setMediaLoading((prev) => ({ ...prev, [i]: false }));
              delete fetchControllers.current[i];
            })
            .catch(() => {
              setMediaLoading((prev) => ({ ...prev, [i]: false }));
              delete fetchControllers.current[i];
            });
        }
      }
    });

    return () => {
      // abort any ongoing fetches when prefetch window changes/unmounts
      Object.values(fetchControllers.current).forEach((ac) => ac && ac.abort());
      fetchControllers.current = {};
    };
  }, [questions, mediaPrefetchLimit]);

  // Scroll to current question once when exam starts
  useEffect(() => {
    if (!isStart || didScrollOnEnterRef.current) return;
    const vp = viewportRef.current;
    if (!vp) return;

    // Scroll to current question section (handles exam restore)
    const sections = Array.from(vp.querySelectorAll('.fast-track-exam__question')) as HTMLElement[];
    const target = sections[questionIndex];
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    didScrollOnEnterRef.current = true;
  }, [isStart, questionIndex]);

  // When all questions are answered, automatically finish the exam
  useEffect(() => {
    if (!isStart || totalQuestions === 0) return;
    const answeredCount = Object.keys(answerResults).length;
    if (answeredCount === totalQuestions && answeredCount > 0) {
      finishExam();
    }
  }, [answerResults, totalQuestions, isStart, finishExam]);

  // Helper: scroll viewport so the target question section is visible
  const scrollToQuestion = useCallback(
    (index: number) => {
      const vp = viewportRef.current;
      if (!vp) return;
      const sections = Array.from(vp.querySelectorAll('.fast-track-exam__question')) as HTMLElement[];
      const target = sections[index];
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    [],
  );

  // Navigate to next/previous question on scroll intent
  const navigateQuestion = useCallback(
    (direction: 'next' | 'prev') => {
      if (isSnapCooldownRef.current) return;

      if (direction === 'next') {
        const nextIndex = questionIndex + 1;
        if (nextIndex > maxVisibleIndex) return;

        if (showSwipeNote) {
          setShowSwipeNote(false);
        }

        setQuestionIndex(nextIndex);
        scrollToQuestion(nextIndex);
      } else {
        const prevIndex = questionIndex - 1;
        if (prevIndex < 0) return;

        setQuestionIndex(prevIndex);
        scrollToQuestion(prevIndex);
      }

      // Cooldown to prevent rapid-fire navigation
      isSnapCooldownRef.current = true;
      setTimeout(() => {
        isSnapCooldownRef.current = false;
      }, 600);
    },
    [questionIndex, maxVisibleIndex, showSwipeNote, scrollToQuestion],
  );

  // Wheel event: snap to next/previous question on scroll
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();

      if (Math.abs(e.deltaY) < 5) return; // ignore tiny movements

      if (e.deltaY > 0) {
        navigateQuestion('next');
      } else {
        navigateQuestion('prev');
      }
    };

    vp.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      vp.removeEventListener('wheel', onWheel);
    };
  }, [navigateQuestion]);

  // Touch events: snap to next/previous question on swipe
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;

    const onTouchStart = (e: TouchEvent) => {
      touchStartYRef.current = e.touches[0].clientY;
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (touchStartYRef.current === null) return;

      const deltaY = touchStartYRef.current - e.changedTouches[0].clientY;
      touchStartYRef.current = null;

      const SWIPE_THRESHOLD = 30;

      if (Math.abs(deltaY) < SWIPE_THRESHOLD) return;

      if (deltaY > 0) {
        navigateQuestion('next');
      } else {
        navigateQuestion('prev');
      }
    };

    vp.addEventListener('touchstart', onTouchStart, { passive: true });
    vp.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      vp.removeEventListener('touchstart', onTouchStart);
      vp.removeEventListener('touchend', onTouchEnd);
    };
  }, [navigateQuestion]);

  const handleSetAnswer = async (answer: string) => {
    if (isAnswerLoading || isFinishing || !currentQuestion || answerResults[questionIndex]) {
      return;
    }

    setIsAnswerLoading(true);

    // optimistic UI: mark selected immediately so button shows active state
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionIndex]: answer,
    }));

    try {
      const response = await getDrivingTestAnswer({
        answer,
        questionUuid: currentQuestion?.uuid,
        drivingTestUuid: drivingTest?.uuid,
      });

      if (response === 422) {
        setIsAnswerLoading(false);
        navigate({ pathname: ROUTES.LOGIN_PAGE });
        return;
      }

      const mappedUserAnswer = mapStoredAnswerToOption(currentQuestion, response?.userAnswer);
      const mappedCorrectAnswer = mapStoredAnswerToOption(currentQuestion, response?.correctAnswer);

      setAnswerResults((prev) => ({
        ...prev,
        [questionIndex]: {
          isAnsweredCorrectly: Boolean(response?.isAnsweredCorrectly),
          correctAnswer: mappedCorrectAnswer,
          userAnswer: mappedUserAnswer,
        },
      }));

      setShowSwipeNote(true);

      // reveal next question after answering current
      setMaxVisibleIndex((prev) => Math.max(prev, questionIndex + 1));
    } catch (err) {
      // revert optimistic selection on error
      setSelectedAnswers((prev) => {
        const copy = { ...prev };
        delete copy[questionIndex];
        return copy;
      });
    } finally {
      setIsAnswerLoading(false);
    }
  };

  const handleSaveQuestion = async () => {
    if (!currentQuestion?.uuid || isBookmarkLoading) {
      return;
    }

    setIsBookmarkLoading(true);

    const response = await saveTask({
      taskUuid: currentQuestion.uuid,
      isQuestionSaved: isCurrentQuestionSaved,
    });

    if (response) {
      setSavedQuestions((prev) => ({
        ...prev,
        [currentQuestion.uuid]: !isCurrentQuestionSaved,
      }));
    }

    setIsBookmarkLoading(false);
  };

  const renderQuestionMedia = (filePath, shouldLoadMedia: boolean, isActiveQuestion: boolean, questionIndex: number) => {
    if (!filePath) {
      return null;
    }

    if (!shouldLoadMedia) {
      return <div className="fast-track-exam__media-placeholder" />;
    }

    const fileName = filePath.substring(filePath.lastIndexOf('/') + 1);

    const mediaEl = fileName.endsWith('.mp4') ? (
      <video
        ref={(node) => {
          videoRefs.current[questionIndex] = node;
        }}
        src={filePath}
        controls={false}
        autoPlay={isActiveQuestion}
        muted
        playsInline
        preload={shouldLoadMedia ? 'auto' : 'none'}
        onLoadedData={() => setMediaLoading((prev) => ({ ...prev, [questionIndex]: false }))}
        onError={() => setMediaLoading((prev) => ({ ...prev, [questionIndex]: false }))}>
        Twoja przeglądarka nie obsługuje elementu video.
      </video>
    ) : fileName.endsWith('.webp') || fileName.match(/\.(jpe?g|png|gif|webp)$/i) ? (
      <img
        className="fast-track-exam__img"
        src={filePath}
        alt=""
        loading={shouldLoadMedia ? 'eager' : 'lazy'}
        decoding="async"
        onLoad={() => setMediaLoading((prev) => ({ ...prev, [questionIndex]: false }))}
        onError={() => setMediaLoading((prev) => ({ ...prev, [questionIndex]: false }))}
      />
    ) : (
      <div className="fast-track-exam__empty-media">
        <SVG type={SVG_TYPE.NO_CAMERA} />
        Brak mediów do pytania
      </div>
    );

    return (
      <div className="fast-track-exam__media-wrap">
        {mediaEl}
        {fileName && (fileName.endsWith('.mp4') || fileName.match(/\.(jpe?g|png|gif|webp)$/i)) && mediaLoading[questionIndex] && (
          <div className="fast-track-exam__media-loader">
            <div className="fast-track-exam__spinner" />
          </div>
        )}
      </div>
    );
  };

  const getOptionState = (optionValue: string, selectedAnswer: string, result?: { isAnsweredCorrectly: boolean; correctAnswer?: string }) => {
    const isSelected = selectedAnswer === optionValue;
    const isCorrect = Boolean(result) && result?.correctAnswer === optionValue;
    const isWrongSelected = Boolean(result) && isSelected && !result?.isAnsweredCorrectly;

    return {
      isSelected,
      isCorrect,
      isWrongSelected,
      showCheckBadge: Boolean(result) && isCorrect,
      showWrongBadge: isWrongSelected,
    };
  };

  return isStart && currentQuestion ? (
    <div className="fast-track-exam">
      {isFinishing && (
        <div className="fast-track-exam__finishing-overlay">
          <Loader />
          <p>Zapisywanie wyniku...</p>
        </div>
      )}
      <div className="fast-track-exam__top">
        <div className="fast-track-exam__meta-group">
          <div className="fast-track-exam__meta">
            <p className="paragraph">Kategoria</p>
            <div className="box box--small">{drivingTest?.category}</div>
          </div>
          <div className="fast-track-exam__meta">
            <p className="paragraph">Pytania:</p>
            <div className="fast-track-exam__counter">
              {questionIndex + 1} z {totalQuestions}
            </div>
          </div>
        </div>
        <button type="button" className="fast-track-exam__close" onClick={finishExam} aria-label="Przerwij egzamin" disabled={isFinishing}>
          <SVG type={SVG_TYPE.CLOSE_BOLD} />
        </button>
      </div>

      <div className="fast-track-exam__viewport">
        <div className="fast-track-exam__track" ref={viewportRef}>
          {questions.map((question, index) => {
            const selectedAnswer = selectedAnswers[index];
            const result = answerResults[index];
            const isBlocked = index > maxVisibleIndex;
            const shouldLoadMedia = index <= mediaPrefetchLimit;
            const isActiveQuestion = index === questionIndex;

            return (
              <section
                key={question?.uuid || index}
                className={`fast-track-exam__question ${isBlocked ? 'fast-track-exam__question--blocked' : index === questionIndex ? 'fast-track-exam__question--active' : 'fast-track-exam__question--inactive'}`}>
                <div className="fast-track-exam__question-inner">
                  <div className="fast-track-exam__photo">{renderQuestionMedia(question?.filePath, shouldLoadMedia, isActiveQuestion, index)}</div>

                  <div className="fast-track-exam__content-card">
                    <div className="fast-track-exam__question-header">
                      <p className="fast-track-exam__question-text paragraph">{question?.question}</p>
                      <button
                        type="button"
                        className={`fast-track-exam__bookmark ${isCurrentQuestionSaved ? 'fast-track-exam__bookmark--active' : ''}`}
                        aria-label="Zapisz pytanie"
                        onClick={handleSaveQuestion}
                        disabled={isBookmarkLoading}>
                        <SVG type={SVG_TYPE.SAVE_LABEL} />
                      </button>
                    </div>

                    {question?.type === QUESTION_TYPE.basic ? (
                      <div className="fast-track-exam__answers fast-track-exam__answers--basic">
                        {[
                          { value: 'T', label: 'TAK' },
                          { value: 'N', label: 'NIE' },
                        ].map((option) => {
                          const state = getOptionState(option.value, selectedAnswer, result);

                          return (
                            <button
                              key={option.value}
                              type="button"
                              disabled={Boolean(result) || isAnswerLoading}
                              onClick={() => handleSetAnswer(option.value)}
                              className={`fast-track-exam__basic-answer ${state.isSelected ? 'fast-track-exam__basic-answer--active' : ''} ${
                                state.isCorrect ? 'fast-track-exam__basic-answer--correct' : ''
                              } ${state.isWrongSelected ? 'fast-track-exam__basic-answer--wrong' : ''}`}>
                              <span className="fast-track-exam__answer-letter">{option.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="fast-track-exam__answers fast-track-exam__answers--specialized">
                        {[
                          { value: 'answerA', label: 'A', text: question.answerA },
                          { value: 'answerB', label: 'B', text: question.answerB },
                          { value: 'answerC', label: 'C', text: question.answerC },
                        ].map((option) => {
                          const state = getOptionState(option.value, selectedAnswer, result);

                          return (
                            <button
                              key={option.value}
                              type="button"
                              disabled={Boolean(result) || isAnswerLoading}
                              onClick={() => handleSetAnswer(option.value)}
                              className={`fast-track-exam__answer-row ${state.isSelected ? 'fast-track-exam__answer-row--active' : ''} ${
                                state.isCorrect ? 'fast-track-exam__answer-row--correct' : ''
                              } ${state.isWrongSelected ? 'fast-track-exam__answer-row--wrong' : ''}`}>
                              <span className="fast-track-exam__answer-letter">
                                {option.label}
                                {state.showCheckBadge && (
                                  <span className="fast-track-exam__answer-badge fast-track-exam__answer-badge--success">
                                    <SVG type={SVG_TYPE.CIRCLE_GREEN} />
                                  </span>
                                )}
                                {state.showWrongBadge && (
                                  <span className="fast-track-exam__answer-badge fast-track-exam__answer-badge--error">
                                    <SVG type={SVG_TYPE.CIRCLE_RED} />
                                  </span>
                                )}
                              </span>
                              <span className="paragraph">{option.text}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {currentQuestionResult && showSwipeNote && (
                    <div className="fast-track-exam__swipe-note">
                      <SVG type={SVG_TYPE.FINGER_SWIPE} className="fast-track-exam__swipe-svg" />
                      <p>Przesuń palcem żeby zobaczyć pytanie</p>
                    </div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  ) : null;
}

export default FastTrackExaminationPage;
